import React, { useRef, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ArrowDownLeft, ArrowUpRight, ChevronDown, Paperclip, Reply } from "lucide-react";
import type { ComposeDefaults } from "./ComposeEmailDialog";
import type { DealEmailRecord } from "@/types/email";

/** One email conversation on a deal */
export interface EmailThread {
  id: string;
  subject: string;
  messages: DealEmailRecord[];
  last: DealEmailRecord;
  unread: number;
}

const DEAL_TAG_RE = /\s*\[Deal #[0-9a-f]{8}\]/i;
const cleanSubject = (s: string) => (s || "(no subject)").replace(DEAL_TAG_RE, "").trim() || "(no subject)";
const replySubject = (s: string) => (/^re:/i.test(s) ? s : `Re: ${s}`);

export const formatEmailDate = (value: string) =>
  new Date(value).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });

/** Groups emails (oldest first) into conversations */
export function groupEmailThreads(emails: DealEmailRecord[]): EmailThread[] {
  const map = new Map<string, DealEmailRecord[]>();
  for (const e of emails) {
    const list = map.get(e.thread_id) || [];
    list.push(e);
    map.set(e.thread_id, list);
  }
  return Array.from(map.entries()).map(([id, messages]) => {
    const ordered = [...messages].sort(
      (a, b) => new Date(a.email_date).getTime() - new Date(b.email_date).getTime(),
    );
    return {
      id,
      subject: cleanSubject(ordered[0]!.subject),
      messages: ordered,
      last: ordered[ordered.length - 1]!,
      unread: ordered.filter((m) => !m.is_read).length,
    };
  });
}

/** Compose defaults for replying to the latest message of a thread */
export function replyDefaults(t: EmailThread, ownAddress?: string | null): ComposeDefaults {
  const lastIncoming = [...t.messages].reverse().find((m) => m.direction === "IN");
  const own = (ownAddress || "").toLowerCase();
  return {
    to: lastIncoming ? lastIncoming.from_address : t.last.to_addresses.join(", "),
    cc: (lastIncoming || t.last).cc_addresses.filter((a) => a.toLowerCase() !== own).join(", "),
    subject: replySubject(t.last.subject),
    replyToEmailId: t.last.id,
    quote: {
      author: t.last.direction === "IN" ? t.last.from_name || t.last.from_address : "You",
      date: formatEmailDate(t.last.email_date),
      text: t.last.body_text || t.last.snippet || "",
    },
  };
}

interface EmailThreadItemProps {
  thread: EmailThread;
  canReply: boolean;
  onReply: (t: EmailThread) => void;
  onOpened: (t: EmailThread) => void;
}

/** An email conversation rendered as an Activity Timeline entry; click to expand the messages */
export function EmailThreadItem({ thread: t, canReply, onReply, onOpened }: EmailThreadItemProps) {
  const [open, setOpen] = useState(false);
  const last = t.last;
  const participants =
    last.direction === "IN" ? `From ${last.from_name || last.from_address}` : `To ${last.to_addresses.join(", ")}`;

  const toggle = () => {
    setOpen((v) => !v);
    if (!open && t.unread > 0) onOpened(t);
  };

  return (
    <div className="relative group">
      <div className="absolute -left-[21px] top-2 size-2.5 rounded-full ring-4 ring-card bg-rose-500" />

      <div className="bg-background border border-border/60 rounded-xl hover:border-border transition-colors shadow-2xs">
        <button type="button" onClick={toggle} className="w-full text-left p-3 space-y-1.5">
          <div className="flex items-center justify-between text-xs gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <Badge
                variant="outline"
                className="text-[10px] font-semibold tracking-wide bg-rose-500/10 text-rose-700 dark:text-rose-400 border-rose-500/30 shrink-0"
              >
                EMAIL
              </Badge>
              <span className={`text-xs text-foreground truncate ${t.unread ? "font-bold" : "font-semibold"}`}>{t.subject}</span>
              {t.messages.length > 1 && (
                <span className="text-[10px] px-1.5 rounded-full bg-muted text-muted-foreground shrink-0">{t.messages.length}</span>
              )}
              {t.unread > 0 && (
                <span className="text-[10px] px-1.5 rounded-full bg-primary text-primary-foreground shrink-0">{t.unread} new</span>
              )}
            </div>
            <div className="flex items-center gap-1.5 shrink-0">
              <span className="text-[11px] text-muted-foreground font-mono">{formatEmailDate(last.email_date)}</span>
              <ChevronDown className={`size-3.5 text-muted-foreground transition-transform ${open ? "" : "-rotate-90"}`} />
            </div>
          </div>
          {!open && (
            <p className="text-xs text-foreground/80 truncate">
              <span className="text-muted-foreground">{participants}: </span>
              {last.snippet}
            </p>
          )}
        </button>

        {open && (
          <div className="px-3 pb-3 space-y-2">
            {t.messages.map((m) => (
              <EmailMessage key={m.id} email={m} />
            ))}
            {canReply && (
              <div className="flex justify-end">
                <Button size="sm" variant="outline" className="h-8 gap-1.5 text-xs" onClick={() => onReply(t)}>
                  <Reply className="size-3.5" />
                  Reply
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function EmailMessage({ email }: { email: DealEmailRecord }) {
  const incoming = email.direction === "IN";
  const sender = incoming
    ? email.from_name || email.from_address
    : email.user
      ? `${email.user.first_name} ${email.user.last_name || ""}`.trim()
      : email.from_address;

  return (
    <div className={`rounded-xl border ${incoming ? "bg-muted/30" : "bg-primary/5 border-primary/20"}`}>
      <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 px-3 py-2 border-b text-xs">
        {incoming ? <ArrowDownLeft className="size-3.5 text-emerald-600" /> : <ArrowUpRight className="size-3.5 text-primary" />}
        <span className="font-semibold">{sender}</span>
        <span className="text-muted-foreground truncate">
          to {email.to_addresses.join(", ")}
          {email.cc_addresses.length > 0 && `, cc ${email.cc_addresses.join(", ")}`}
          {!incoming && (email.bcc_addresses?.length || 0) > 0 && `, bcc ${email.bcc_addresses!.join(", ")}`}
        </span>
        <span className="ml-auto text-muted-foreground">{formatEmailDate(email.email_date)}</span>
      </div>
      <div className="px-3 py-2">
        {email.body_html ? (
          <EmailHtml html={email.body_html} />
        ) : (
          <div className="text-sm whitespace-pre-wrap break-words">{email.body_text || email.snippet}</div>
        )}
      </div>
      {email.attachments.length > 0 && (
        <div className="flex flex-wrap gap-1.5 px-3 pb-2">
          {email.attachments.map((a, i) => (
            <a
              key={i}
              href={`/api/DealEmail/Attachment/${email.id}/${i}`}
              className="flex items-center gap-1 text-[11px] rounded-md border bg-card px-2 py-1 hover:bg-muted"
            >
              <Paperclip className="size-3" />
              {a.file_name}
              <span className="text-muted-foreground">({Math.ceil(a.size / 1024)} KB)</span>
            </a>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Email HTML (already sanitized on the server) is rendered in a sandboxed iframe with scripts disabled,
 * so its styles can't leak into the CRM and nothing in it can run.
 */
function EmailHtml({ html }: { html: string }) {
  const ref = useRef<HTMLIFrameElement>(null);
  const [height, setHeight] = useState(120);
  const doc = `<!doctype html><html><head><meta charset="utf-8"><base target="_blank"><style>body{margin:0;font-family:Arial,Helvetica,sans-serif;font-size:14px;color:#222;background:#fff;word-wrap:break-word}img{max-width:100%;height:auto}blockquote{margin:0 0 0 8px;padding-left:8px;border-left:2px solid #ccc;color:#555}</style></head><body>${html}</body></html>`;

  const fit = () => {
    const body = ref.current?.contentDocument?.documentElement;
    if (body) setHeight(Math.min(Math.max(body.scrollHeight + 4, 40), 600));
  };

  return (
    <iframe
      ref={ref}
      title="Email content"
      srcDoc={doc}
      sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
      onLoad={fit}
      className="w-full rounded-md bg-white"
      style={{ height, border: 0 }}
    />
  );
}
